import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { withCallState } from '@shared/store-features/call-state-feature.ts';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import { CallStateEnum } from '@models/call-state.model.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import {
  editOrganization,
  getCurrencies,
  getProfile,
  getPmsConfig,
  organizationBranding,
  uploadFile
} from '@api/profile.api.ts';
import { updateHotelWifiOrganizationConfig } from '@shared/api/hotel-wifi/organization-config-api';
import {
  OrganizationInfoParams,
  PROFILE_INITIAL_STATE,
  ProfileState
} from '@store/profile-store/profile.model.ts';
import axios from 'axios';
import useOrganizationBrandingStore from '@store/organization-branding-store/organization-branding.store.ts';
import { getRootDomainFromHost } from '@shared/utils/hostname-utils';
import { getUsersBranding } from '@shared/api/auth.api';

const { showSnackbar } = useSnackbarStore.getState();

const useProfileStore = create<ProfileState>()(
  devtools(
    (set, getState) => ({
      ...PROFILE_INITIAL_STATE,
      ...withCallState(getState),

      getProfile: async () => {
        set({ callState: CallStateEnum.Loading });
        try {
          const data = await getProfile();
          set({ callState: CallStateEnum.Loaded });
          set({ profileData: data });
          const selectedCurrency = data.organization?.currency;
          if (selectedCurrency) {
            set({ selectedCurrency });
          }

          const { primaryColor, image } = data.organization;
          const { setPrimaryColor, setImage } =
            useOrganizationBrandingStore.getState();

          if (primaryColor) setPrimaryColor(primaryColor);
          if (image) setImage(image);

          if (!primaryColor || !image) {
            const rootDomain = getRootDomainFromHost();
            const res: any = await getUsersBranding(rootDomain);
            if (res.primaryColor && !primaryColor)
              setPrimaryColor(res.primaryColor);
            if (res.logo && !image) setImage(res.logo);
          }
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      getCurrencies: async () => {
        try {
          const data = await getCurrencies();
          set({ currencies: data });
        } catch (error) {
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      uploadAvatar: async (body) => {
        set({ uploadImageCallState: CallStateEnum.Loading });
        try {
          await uploadFile(body);
          set({ uploadImageCallState: CallStateEnum.Loaded });
        } catch (error) {
          set({ uploadImageCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      organizationBranding: async ({
        data,
        id,
        handleClose
      }: OrganizationInfoParams) => {
        set({ organizationBrandingCallState: CallStateEnum.Loading });
        try {
          await organizationBranding(data as any, id as number);

          getState().getProfile();
          const { primaryColor, image } = data;
          const { setPrimaryColor, setImage } =
            useOrganizationBrandingStore.getState();

          if (primaryColor) setPrimaryColor(primaryColor);
          if (image) setImage(image);

          set({ organizationBrandingCallState: CallStateEnum.Loaded });
          handleClose?.();
          showSnackbar('snackbar_update_text', SnackbarType.Success);
        } catch (error) {
          set({ organizationBrandingCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      editOrganizationInfo: async ({
        data,
        id,
        handleClose
      }: OrganizationInfoParams) => {
        set({ organizationInfoCallState: CallStateEnum.Loading });
        try {
          await editOrganization(data as any, id as number);

          void updateHotelWifiOrganizationConfig({ currencyCode: data?.currencyCode ?? null }).catch(() => undefined);

          getState().getProfile();
          set({ organizationInfoCallState: CallStateEnum.Loaded });
          handleClose?.();
          showSnackbar('snackbar_update_text', SnackbarType.Success);
        } catch (error) {
          set({ organizationInfoCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      getPmsConfig: async () => {
        set({ pmsConfigCallState: CallStateEnum.Loading });
        try {
          const orgId = getState().profileData?.orgId;
          if (!orgId) {
            set({ pmsConfigCallState: CallStateEnum.Loaded });
            return;
          }
          const data = await getPmsConfig(orgId);
          set({ activePmsConfig: data, pmsConfigCallState: CallStateEnum.Loaded });
        } catch (error) {
          set({ pmsConfigCallState: CallStateEnum.Loaded });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      isPmsConfigLoading: () =>
        getState().pmsConfigCallState === CallStateEnum.Loading,

      setPreviewUrl: (url) => set({ previewUrl: url }),

      isOrganizationInfoLoading: () =>
        getState().organizationInfoCallState === CallStateEnum.Loading,
      isOrganizationInfoLoaded: () =>
        getState().organizationInfoCallState === CallStateEnum.Loaded,
      isOrganizationInfoError: () =>
        getState().organizationInfoCallState === CallStateEnum.Error,
      isUploadImageLoading: () =>
        getState().uploadImageCallState === CallStateEnum.Loading,
      isUploadImageLoaded: () =>
        getState().uploadImageCallState === CallStateEnum.Loaded,
      isUploadImageError: () =>
        getState().uploadImageCallState === CallStateEnum.Error,
      isOrganizationBrandingLoading: () =>
        getState().organizationBrandingCallState === CallStateEnum.Loading,
      isOrganizationBrandingLoaded: () =>
        getState().organizationBrandingCallState === CallStateEnum.Loaded,
      isOrganizationBrandingError: () =>
        getState().organizationBrandingCallState === CallStateEnum.Error
    }),
    { name: 'ProfileStore' }
  )
);

export default useProfileStore;
